import { createContext, useContext, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BookingCtx = createContext(null);
export const useBooking = () => useContext(BookingCtx);

export function BookingProvider({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <BookingCtx.Provider value={{ open, setOpen }}>
      {children}
      <BookingModal open={open} onClose={() => setOpen(false)} />
    </BookingCtx.Provider>
  );
}

const field = "w-full bg-black/50 border border-white/15 rounded-xl px-4 py-3 text-white placeholder:text-white/30 outline-none focus:border-gold focus:ring-2 focus:ring-gold/40 transition";

function BookingModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", goal: "", preferred_time: "", experience: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/free-intro`, form);
      setDone(true);
      toast.success("Booking received! We'll be in touch soon.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => { setDone(false); setForm({ name: "", email: "", phone: "", goal: "", preferred_time: "", experience: "" }); }, 300);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="booking-modal"
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={close} />
          <motion.div
            className="relative w-full max-w-lg bg-charcoal border border-white/10 rounded-3xl p-8 md:p-10 max-h-[90vh] overflow-y-auto"
            initial={{ y: 40, scale: 0.96, opacity: 0 }} animate={{ y: 0, scale: 1, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
            transition={{ type: "spring", damping: 24, stiffness: 260 }}
          >
            <button onClick={close} data-testid="booking-close" className="absolute top-5 right-5 text-white/50 hover:text-gold transition">
              <X size={22} />
            </button>

            {done ? (
              <div className="text-center py-8" data-testid="booking-success">
                <div className="mx-auto w-16 h-16 rounded-full bg-gold flex items-center justify-center mb-6">
                  <Check size={30} className="text-black" />
                </div>
                <h3 className="font-display text-3xl text-white uppercase">You're in!</h3>
                <p className="mt-3 text-lightgrey">Thanks {form.name.split(" ")[0]}. A coach will reach out within one business day to lock in your free intro. Get excited.</p>
                <button onClick={close} className="mt-8 rounded-full bg-gold text-black font-bold px-8 py-3 hover:bg-white transition">Done</button>
              </div>
            ) : (
              <>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold">Free Intro</p>
                <h3 className="font-display text-3xl md:text-4xl text-white uppercase mt-2 leading-none">Book your first visit</h3>
                <p className="mt-3 text-lightgrey text-sm">No pressure, no obligation. Just a friendly chat about your goals.</p>
                <form onSubmit={submit} className="mt-6 space-y-4" data-testid="booking-form">
                  <input className={field} placeholder="Full name*" value={form.name} onChange={set("name")} data-testid="booking-name" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input className={field} placeholder="Email*" type="email" value={form.email} onChange={set("email")} data-testid="booking-email" />
                    <input className={field} placeholder="Phone*" value={form.phone} onChange={set("phone")} data-testid="booking-phone" />
                  </div>
                  <input className={field} placeholder="Best time to reach you (optional)" value={form.preferred_time} onChange={set("preferred_time")} data-testid="booking-time" />
                  <textarea className={field + " resize-none"} rows={3} placeholder="What's your goal? (optional)" value={form.goal} onChange={set("goal")} data-testid="booking-goal" />
                  <button type="submit" disabled={loading} className="w-full rounded-full bg-gold text-black font-bold py-4 hover:bg-white transition disabled:opacity-60" data-testid="booking-submit">
                    {loading ? "Sending…" : "Claim My Free Intro"}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
